import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Globe, MapPin, Share2, PenTool, CreditCard, BookOpen, MessageCircle, Search, Sparkles,
  CheckCircle2, ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { SERVICES, CUSTOM_SOLUTION_COPY, BRAND } from "../data/site";

const ICONS = { Globe, MapPin, Share2, PenTool, CreditCard, BookOpen, MessageCircle, Search, Sparkles };

export default function Services() {
  const { hash } = useLocation();
  const whatsappHref = `https://wa.me/${BRAND.whatsappNumber}?text=${encodeURIComponent("Hi Zyvora Digital, I'd like to discuss my project.")}`;

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [hash]);

  return (
    <div className="bg-[#0A0A0A] text-white pt-28 pb-24">
      {/* Header */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute -top-24 -right-32 h-[440px] w-[440px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-3">Our Services</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] max-w-4xl">
            Full-stack digital services to{" "}
            <span className="text-gradient-brand">build, market & scale</span>
          </h1>
          <p className="mt-6 text-lg text-gray-300 max-w-2xl">
            Every service below can be tailored to your industry, stage and budget. Reach out and we'll craft a plan that fits.
          </p>
        </div>
      </section>

      {/* Custom Solution banner */}
      <section data-testid="services-custom-banner" className="py-6">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-3xl border border-white/10 bg-[#121212] p-8 md:p-10 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-brand opacity-10" />
            <div className="relative grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2">
                <h3 className="font-display text-2xl md:text-3xl font-bold leading-snug">
                  {CUSTOM_SOLUTION_COPY}
                </h3>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-3">
                <Link to="/contact" data-testid="services-banner-cta">
                  <Button className="w-full bg-gradient-brand text-[#0A0A0A] rounded-full h-12 px-6 font-semibold">
                    Request Quote <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href={whatsappHref} target="_blank" rel="noreferrer" data-testid="services-banner-whatsapp">
                  <Button variant="outline" className="w-full border-white/15 bg-white/5 hover:bg-white/10 text-white rounded-full h-12 px-6 font-semibold">
                    <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Us
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-20">
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.icon] || Sparkles;
            return (
              <div key={s.slug} id={s.slug} data-testid={`service-block-${s.slug}`} className="scroll-mt-28">
                <div className="grid lg:grid-cols-12 gap-10 mb-10">
                  <div className="lg:col-span-5">
                    <div className="inline-flex items-center gap-3 mb-5">
                      <div className="h-12 w-12 rounded-xl bg-gradient-brand-soft border border-white/10 flex items-center justify-center">
                        <Icon className="h-6 w-6 text-[#D4AF37]" />
                      </div>
                      <Badge className="bg-white/5 text-[#F5D67B] border border-white/10">0{idx + 1}</Badge>
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-4">{s.title}</h2>
                    <p className="text-gray-300 leading-relaxed mb-6">{s.desc}</p>
                    <Link to="/contact" data-testid={`service-cta-${s.slug}`}>
                      <Button className="bg-gradient-brand text-[#0A0A0A] rounded-full h-11 px-6 font-semibold">
                        Request Quote <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                  <div className="lg:col-span-7 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {s.tiers.map((t, i) => (
                      <div
                        key={i}
                        data-testid={`tier-${s.slug}-${i}`}
                        className={`relative rounded-2xl p-6 flex flex-col border transition-all ${
                          t.popular
                            ? "border-[#D4AF37]/60 bg-[#1A1A1A] shadow-[0_0_40px_-10px_rgba(212,175,55,0.4)]"
                            : "border-white/10 bg-[#121212] hover:border-[#D4AF37]/35"
                        }`}
                      >
                        {t.popular && (
                          <span className="absolute -top-3 left-6 text-[10px] font-bold tracking-widest uppercase px-3 py-1 rounded-full bg-gradient-brand text-[#0A0A0A]">
                            Most Recommended
                          </span>
                        )}
                        <h3 className="font-display text-lg font-semibold mb-4">{t.name}</h3>
                        <ul className="space-y-2.5 mb-6 flex-1">
                          {t.features.map((f, j) => (
                            <li key={j} className="flex items-start gap-2 text-sm text-gray-300">
                              <CheckCircle2 className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
                              {f}
                            </li>
                          ))}
                        </ul>
                        <Link to="/contact" data-testid={`tier-cta-${s.slug}-${i}`}>
                          <Button
                            className={`w-full rounded-full h-11 font-semibold ${
                              t.popular ? "bg-gradient-brand text-[#0A0A0A]" : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
                            }`}
                          >
                            Discuss Your Project
                          </Button>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-16">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="rounded-3xl bg-gradient-brand p-10 md:p-14 text-center glow-blue">
            <h3 className="font-display text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-3">
              Not sure which service fits?
            </h3>
            <p className="text-black/70 mb-7 max-w-2xl mx-auto">
              Book a free consultation and we'll recommend the right service mix based on your business goals.
            </p>
            <Link to="/contact" data-testid="services-cta-button">
              <Button className="bg-white text-[#0A0A0A] hover:bg-[#F5D67B]/20 rounded-full h-12 px-7 font-bold">
                Get Free Consultation <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
